import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { blogPosts, blogCategories, sortPostsByDate, getFeaturedPosts } from '@/mocks/blogData';
import BlogCard from './components/BlogCard';
import Breadcrumbs from './components/Breadcrumbs';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import useSeo, { SITE_URL } from '@/hooks/useSeo';

export default function BlogPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const blogUrl = `${SITE_URL}/blog`;

  useSeo({
    title: 'TNT Coach AI Blog – Coaching Insights & Personal Growth Resources',
    description: 'Read the TNT Coach AI blog for practical coaching insights on mindset, productivity, and personal growth. Expert articles to help you think clearly, act confidently, and transform daily.',
    canonical: blogUrl,
    url: blogUrl,
    image: 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20brand%20banner%20in%20deep%20charcoal%20black%20and%20warm%20golden%20amber%20tones%2C%20soft%20gradient%20light%20rays%20and%20minimal%20geometric%20shapes%20suggesting%20upward%20growth%20and%20personal%20transformation%2C%20premium%20editorial%20style%2C%20generous%20negative%20space%2C%20sophisticated%20modern%20aesthetic%2C%20high%20detail&width=1200&height=630&seq=tnt-coach-og-image&orientation=landscape',
  });

  const sortedPosts = sortPostsByDate(blogPosts);
  const featuredPosts = getFeaturedPosts();

  const filteredPosts = sortedPosts.filter((post) => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = !searchQuery
      || post.title.toLowerCase().includes(searchQuery.toLowerCase())
      || post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      || post.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const blogStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'TNT Coach AI Blog',
    description: 'Practical coaching insights on mindset, productivity, and personal growth.',
    url: typeof window !== 'undefined' ? window.location.origin + '/blog' : '/blog',
    author: {
      '@type': 'Organization',
      name: 'TNT Coach AI',
    },
    blogPost: sortedPosts.slice(0, 10).map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      url: typeof window !== 'undefined' ? window.location.origin + '/blog/' + post.slug : '/blog/' + post.slug,
      datePublished: post.publishedAt,
      author: {
        '@type': 'Organization',
        name: post.author,
      },
      image: post.coverImage,
    })),
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: blogUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-midnight">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      <Navbar />

      <main>
        <section className="pt-32 pb-16 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Blog' }]} />

            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-champagne-gold/10 text-champagne-gold text-xs font-medium border border-champagne-gold/20 mb-4">
                Blog
              </span>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-warm-white mb-4">
                Insights &amp; Coaching Resources
              </h1>
              <p className="text-silver-grey text-base max-w-xl mx-auto leading-relaxed">
                Practical articles on mindset, productivity, and personal growth — written to help you think clearly and act confidently.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-10">
              <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-2 sm:pb-0">
                {blogCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                      activeCategory === cat
                        ? 'bg-champagne-gold text-midnight'
                        : 'bg-royal-navy/30 text-silver-grey hover:text-warm-white hover:bg-royal-navy/50'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="relative w-full sm:w-64">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-taupe-grey text-sm" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-full bg-royal-navy/20 border border-royal-navy/40 text-warm-white text-sm placeholder:text-taupe-grey focus:outline-none focus:border-champagne-gold/50 transition-colors"
                />
              </div>
            </div>

            {featuredPosts.length > 0 && searchQuery === '' && activeCategory === 'All' && (
              <div className="mb-14">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-champagne-gold mb-5">Featured</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {featuredPosts.map((post) => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </div>
              </div>
            )}

            {filteredPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-royal-navy/30 flex items-center justify-center">
                  <i className="ri-file-search-line text-2xl text-silver-grey" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-warm-white mb-2">No articles found</h3>
                <p className="text-silver-grey text-sm">
                  Try adjusting your search or category filter.
                </p>
              </div>
            )}

            <div className="mt-14 p-8 rounded-lg bg-royal-navy/10 border border-royal-navy/20 text-center">
              <h3 className="text-xl font-heading font-semibold text-warm-white mb-2">
                Want coaching insights delivered to your inbox?
              </h3>
              <p className="text-silver-grey text-sm mb-4">
                Join our newsletter for weekly mindset tips and practical coaching strategies.
              </p>
              <button
                onClick={() => navigate('/#pricing')}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full gold-gradient text-midnight font-semibold text-sm hover:opacity-90 transition-opacity duration-300 whitespace-nowrap cursor-pointer"
              >
                Ask TNT Coach AI
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </section>
      </main>

      <FooterSection />
    </div>
  );
}